import { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  TextField,
  Stack,
  CircularProgress,
  Alert,
  Paper,
  Snackbar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Divider,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { auth } from '../utils/auth';
import { CloudUpload, Description, Help, SaveAlt, Download } from '@mui/icons-material';
import axios from 'axios';
import { saveNote } from '../utils/supabase';
import mermaid from 'mermaid';
import { generateContent } from '../utils/ai';

function DiagramGenerator() {
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [diagramCode, setDiagramCode] = useState('');
  const [diagramType, setDiagramType] = useState('flowchart');
  const [user, setUser] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [showExampleDialog, setShowExampleDialog] = useState(false);
  const [svgContent, setSvgContent] = useState('');
  const diagramRef = useRef(null);

  const [diagramTypes, setDiagramTypes] = useState([
    { value: 'flowchart', label: 'Flowchart', example: 'Create a flowchart showing the user registration process with signup, email verification, and login steps.' },
    { value: 'sequence', label: 'Sequence Diagram', example: 'Create a sequence diagram showing how a user orders an item online with user, website, payment processor, and inventory system.' },
    { value: 'classDiagram', label: 'Class Diagram', example: 'Create a class diagram for a library system with Book, Author, Member, and Librarian classes.' },
    { value: 'entityRelationship', label: 'Entity Relationship', example: 'Create an entity relationship diagram for a school database with Students, Teachers, Courses, and Grades.' },
    { value: 'gantt', label: 'Gantt Chart', example: 'Create a Gantt chart for a software development project with requirements gathering, design, implementation, testing, and deployment phases.' },
    { value: 'mindmap', label: 'Mind Map', example: 'Create a mind map showing the key concepts of machine learning with supervised learning, unsupervised learning, and reinforcement learning branches.' }
  ]);

  // Initialize mermaid
  useEffect(() => {
    mermaid.initialize({
      startOnLoad: false,
      theme: 'default',
      securityLevel: 'loose',
      themeCSS: '.node rect { fill: #66B2FF; stroke: #0066CC; } .edgeLabel { background-color: white; }'
    });
  }, []);

  // Get current user
  useEffect(() => {
    const initUser = async () => {
      try {
        const currentUser = await auth.getCurrentUser();
        setUser(currentUser);
      } catch (error) {
        console.error('Error loading user data:', error);
      }
    };
    initUser();
  }, []);

  // Render diagram when code changes
  useEffect(() => {
    const renderDiagram = async () => {
      if (diagramCode && diagramRef.current) {
        try {
          diagramRef.current.innerHTML = '';
          const { svg } = await mermaid.render('diagram', diagramCode);
          diagramRef.current.innerHTML = svg;
          setSvgContent(svg);
        } catch (err) {
          console.error('Error rendering diagram:', err);
        }
      }
    };
    renderDiagram();
  }, [diagramCode]);

  const handleGenerateDiagram = async () => {
    if (!text.trim()) {
      setError('Please enter a description of the diagram you want to create');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const messages = [
        {
          role: 'system',
          content: `You are an expert at creating diagrams using Mermaid.js syntax. The user will provide a description of the diagram they want to create, and your job is to generate the corresponding Mermaid code. 
      
          The user wants to create a ${diagramType} diagram.
      
          Only return valid Mermaid.js code with no explanations or additional text - just the Mermaid code block without markdown formatting.
          
          Here's the Mermaid syntax for different diagram types:
          
          For flowcharts:
          \`\`\`
          flowchart TD
              A[Start] --> B{Decision}
              B -->|Yes| C[Process]
              B -->|No| D[End]
          \`\`\`
          
          For sequence diagrams:
          \`\`\`
          sequenceDiagram
              participant Alice
              participant Bob
              Alice->>Bob: Hello Bob, how are you?
              Bob-->>Alice: I am good thanks!
          \`\`\`
          
          For class diagrams:
          \`\`\`
          classDiagram
              class Animal {
                  +String name
                  +makeSound() void
              }
              class Dog {
                  +bark() void
              }
              Animal <|-- Dog
          \`\`\`
          
          For entity relationship diagrams:
          \`\`\`
          erDiagram
              CUSTOMER ||--o{ ORDER : places
              ORDER ||--|{ LINE-ITEM : contains
          \`\`\`
          
          For Gantt charts:
          \`\`\`
          gantt
              title A Gantt Diagram
              dateFormat  YYYY-MM-DD
              section Section
              A task           :a1, 2020-01-01, 30d
              Another task     :after a1, 20d
          \`\`\`
          
          For mind maps:
          \`\`\`
          mindmap
              root((Main Topic))
                  Topic 1
                      Subtopic 1
                      Subtopic 2
                  Topic 2
                      Subtopic 3
          \`\`\`
          
          Remember to only return the Mermaid code, without any explanations, comments, or markdown formatting.`
        },
        {
          role: 'user',
          content: text
        }
      ];

      const responseContent = await generateContent(messages);
      
      // Clean up the response to extract just the Mermaid code
      let cleanedDiagramCode = responseContent.trim();
      
      // Remove all code block markers (```mermaid, ```, etc.) and leading/trailing whitespace
      cleanedDiagramCode = cleanedDiagramCode
        .replace(/^```mermaid\s*/i, '')
        .replace(/^```\s*/i, '')
        .replace(/```$/i, '')
        .trim();

      setDiagramCode(cleanedDiagramCode);
    } catch (err) {
      setError(err.message || 'Failed to generate diagram');
    } finally {
      setLoading(false);
    }
  };

  const handleShowExample = () => {
    const selectedDiagramType = diagramTypes.find(type => type.value === diagramType);
    setText(selectedDiagramType?.example || '');
    setShowExampleDialog(false);
  };

  const handleSaveDiagram = async () => {
    if (!user) {
      setError('You must be logged in to save diagrams');
      return;
    }

    try {
      const noteContent = `# ${text.substring(0, 30)}...\n\n\`\`\`mermaid\n${diagramCode}\n\`\`\``;
      await saveNote(user.id, noteContent, `Diagram: ${text.substring(0, 20)}...`);
      setSnackbarMessage('Diagram saved successfully!');
      setSnackbarOpen(true);
    } catch (err) {
      setError('Failed to save diagram');
      console.error('Save error:', err);
    }
  };

  const handleDownloadSVG = () => {
    if (!svgContent) return;
    
    const blob = new Blob([svgContent], { type: 'image/svg+xml' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `diagram-${Date.now()}.svg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    setSnackbarMessage('SVG downloaded successfully');
    setSnackbarOpen(true);
  };

  return (
    <Box sx={{ py: 4 }}>
      <Container maxWidth="lg">
        <Typography variant="h2" gutterBottom>
          AI Diagram Generator
        </Typography>

        <Stack spacing={4}>
          {/* Diagram Type Selection */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom display="flex" alignItems="center">
              Select Diagram Type
              <Tooltip title="Choose the type of diagram you want to create">
                <IconButton size="small" sx={{ ml: 1 }}>
                  <Help fontSize="small" />
                </IconButton>
              </Tooltip>
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="diagram-type-label">Diagram Type</InputLabel>
              <Select
                labelId="diagram-type-label"
                id="diagram-type"
                value={diagramType}
                label="Diagram Type"
                onChange={(e) => setDiagramType(e.target.value)}
                disabled={loading}
              >
                {diagramTypes.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <Button 
              sx={{ mt: 2 }}
              variant="text" 
              onClick={() => setShowExampleDialog(true)}
            >
              Show Example Prompt
            </Button>
          </Paper>

          {/* Description Input Section */}
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Describe Your Diagram
            </Typography>
            <TextField
              fullWidth
              multiline
              rows={4}
              placeholder="Example: Create a flowchart that shows the process of user authentication with login, verification, and access control steps..."
              value={text}
              onChange={(e) => setText(e.target.value)}
              disabled={loading}
            />
          </Paper>

          {/* Error Display */}
          {error && (
            <Alert severity="error" onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {/* Generate Button */}
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <Button
              variant="contained"
              size="large"
              onClick={handleGenerateDiagram}
              disabled={loading || !text.trim()}
              startIcon={loading ? <CircularProgress size={20} /> : <Description />}
            >
              {loading ? 'Generating...' : 'Generate Diagram'}
            </Button>
          </Box>

          {/* Generated Diagram Display */}
          {diagramCode && (
            <Paper sx={{ p: 3 }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" mb={2}>
                <Typography variant="h6">
                  Generated Diagram
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    color="primary"
                    startIcon={<Download />}
                    onClick={handleDownloadSVG}
                  >
                    Download SVG
                  </Button>
                  {user && (
                    <Button
                      variant="contained"
                      color="primary"
                      startIcon={<SaveAlt />}
                      onClick={handleSaveDiagram}
                    >
                      Save Diagram
                    </Button>
                  )}
                </Stack>
              </Stack>
              <Divider sx={{ mb: 3 }} />
              
              {/* Diagram Visualization */}
              <Box sx={{ mb: 3, overflowX: 'auto' }}>
                <Box 
                  ref={diagramRef} 
                  sx={{ 
                    minHeight: '200px',
                    p: 2,
                    bgcolor: '#fafafa',
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    '& svg': {
                      maxWidth: '100%',
                      height: 'auto',
                      display: 'block',
                      margin: '0 auto'
                    }
                  }} 
                />
              </Box>
            </Paper>
          )}
          
          <Snackbar
            open={snackbarOpen}
            autoHideDuration={3000}
            onClose={() => setSnackbarOpen(false)}
            message={snackbarMessage}
          />
        </Stack>
      </Container>

      {/* Example Dialog */}
      <Dialog open={showExampleDialog} onClose={() => setShowExampleDialog(false)}>
        <DialogTitle>Example Prompt</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Here's an example prompt for a {diagramTypes.find(type => type.value === diagramType)?.label || 'diagram'}:
          </Typography>
          <Typography variant="body1">
            {diagramTypes.find(type => type.value === diagramType)?.example || ''}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowExampleDialog(false)}>
            Cancel
          </Button>
          <Button onClick={handleShowExample} variant="contained" color="primary">
            Use This Example
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default DiagramGenerator;